if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();

        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        /*
         * KOR state for the currently opened Quick Add product.
         */
        this.korRule = null;
        this.korRulePromise = null;
        this.korProductId = null;
        this.korMessageTimer = null;
        this.korResubmitting = false;

        /*
         * Capture quantity interactions inside Quick Add.
         *
         * Capture phase lets us stop the + button before Dawn
         * increases beyond the KOR maximum.
         */
        this.modalContent.addEventListener(
          'click',
          this.handleKorQuantityClick.bind(this),
          true
        );

        this.modalContent.addEventListener(
          'input',
          this.handleKorQuantityInput.bind(this),
          true
        );

        this.modalContent.addEventListener(
          'change',
          this.handleKorQuantityInput.bind(this),
          true
        );

        /*
         * Final client-side protection before Add to cart.
         *
         * If KOR is still loading because the customer clicked
         * extremely quickly, wait for the SAME request rather than
         * making another request.
         */
        this.modalContent.addEventListener(
          'submit',
          this.handleKorSubmit.bind(this),
          true
        );
      }

      hide(preventFocus = false) {
        const cartNotification =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        if (cartNotification) {
          cartNotification.setActiveElement(this.openedBy);
        }

        this.clearKorState();

        this.modalContent.innerHTML = '';

        if (preventFocus) {
          this.openedBy = null;
        }

        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');

        const loadingSpinner = opener.querySelector('.loading__spinner');

        if (loadingSpinner) {
          loadingSpinner.classList.remove('hidden');
        }

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(
              responseText,
              'text/html'
            );

            this.productElement = responseHTML.querySelector(
              'section[id^="MainProduct-"]'
            );

            if (!this.productElement) {
              throw new Error(
                'Quick Add product section could not be found.'
              );
            }

            this.productElement.classList.forEach((classApplied) => {
              if (
                classApplied.startsWith('color-') ||
                classApplied === 'gradient'
              ) {
                this.modalContent.classList.add(classApplied);
              }
            });

            this.preventDuplicatedIDs();
            this.removeDOMElements();

            this.setInnerHTML(
              this.modalContent,
              this.productElement.innerHTML
            );

            /*
             * Start KOR lookup in the background.
             *
             * IMPORTANT:
             * We do NOT wait here.
             *
             * The Quick Add modal can open immediately while the
             * quantity rule loads in parallel.
             */
            this.initializeKorQuickAdd(opener);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }

            if (window.ProductModel) {
              window.ProductModel.loadShopifyXR();
            }

            this.removeGalleryListSemantic();
            this.updateImageSizes();
            this.preventVariantURLSwitching();

            super.show(opener);
          })
          .catch((error) => {
            console.error('Quick Add error:', error);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');

            if (loadingSpinner) {
              loadingSpinner.classList.add('hidden');
            }
          });
      }

      /*
       * ============================================================
       * KOR QUICK ADD INTEGRATION
       * ============================================================
       */

      clearKorState() {
        this.korRule = null;
        this.korRulePromise = null;
        this.korProductId = null;
        this.korResubmitting = false;

        if (this.korMessageTimer) {
          clearTimeout(this.korMessageTimer);
          this.korMessageTimer = null;
        }
      }

      initializeKorQuickAdd(opener) {
        this.korRule = null;
        this.korProductId = null;

        /*
         * Do all KOR work in one background Promise.
         */
        const rulePromise = (async () => {
          const productId = await this.getQuickAddProductId(opener);

          if (!productId) {
            console.warn(
              'KOR Quick Add: product ID could not be determined.'
            );

            return null;
          }

          this.korProductId = productId;

          /*
           * Check session cache first.
           *
           * If this product has already been opened once during this
           * browsing session, the limit is available immediately.
           */
          const cachedRule = this.getCachedKorRule(productId);

          if (cachedRule) {
            this.korRule = cachedRule;
            this.applyKorRule();

            console.debug(
              'KOR Quick Add: using cached rule',
              cachedRule
            );
          }

          /*
           * Get current cart because KOR uses it when calculating
           * remaining quantity.
           */
          const cartItems = await fetch('/cart.js', {
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json'
            }
          }).then((response) => {
            if (!response.ok) {
              throw new Error(
                `Cart request failed: ${response.status}`
              );
            }

            return response.json();
          });

          /*
           * Ask KOR using the same endpoint its own product-page
           * integration uses.
           */
          const korResponse = await this.requestKorProductLimit(
            productId,
            cartItems
          );

          const rule = this.extractKorRule(korResponse);

          if (!rule) {
            console.debug(
              'KOR Quick Add: no applicable quantity rule.',
              korResponse
            );

            return null;
          }

          this.korRule = rule;

          this.cacheKorRule(productId, rule);
          this.applyKorRule();

          console.debug(
            'KOR Quick Add: rule loaded',
            rule
          );

          return rule;
        })().catch((error) => {
          console.warn(
            'KOR Quick Add rule lookup failed:',
            error
          );

          /*
           * If we already had a cached rule, keep using it.
           */
          return this.korRule;
        });

        this.korRulePromise = rulePromise;
        rulePromise.then(() => {
          // Only pending lookups should intercept submission. A completed
          // lookup with no rule must allow the original submit event through.
          // Do not clear a newer lookup after the modal has been reopened.
          if (this.korRulePromise === rulePromise) {
            this.korRulePromise = null;
          }
        });

        return rulePromise;
      }

      /*
       * Try to get the Shopify product ID from the injected HTML.
       *
       * If the theme doesn't expose it, use Shopify's lightweight
       * product JSON endpoint.
       */
      async getQuickAddProductId(opener) {
        const possibleElements = this.modalContent.querySelectorAll(
          '[data-product-id]'
        );

        for (const element of possibleElements) {
          const value = element.dataset.productId;

          if (value && /^\d+$/.test(String(value))) {
            return String(value);
          }
        }

        const productIdInput = this.modalContent.querySelector(
          'input[name="product-id"]'
        );

        if (
          productIdInput &&
          /^\d+$/.test(String(productIdInput.value))
        ) {
          return String(productIdInput.value);
        }

        /*
         * Reliable fallback:
         *
         * /products/product-handle.js
         */
        const originalUrl = opener.getAttribute('data-product-url');

        if (!originalUrl) {
          return null;
        }

        const productUrl = new URL(
          originalUrl,
          window.location.origin
        );

        productUrl.search = '';
        productUrl.hash = '';

        productUrl.pathname =
          productUrl.pathname.replace(/\/$/, '') + '.js';

        const productData = await fetch(productUrl.toString(), {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json'
          }
        }).then((response) => {
          if (!response.ok) {
            throw new Error(
              `Product JSON request failed: ${response.status}`
            );
          }

          return response.json();
        });

        return productData?.id
          ? String(productData.id)
          : null;
      }

      /*
       * Call the KOR product endpoint.
       *
       * Prefer jQuery because this makes the request in exactly the
       * same form KOR itself uses.
       */
      requestKorProductLimit(productId, cartItems) {
        const config = {
          locale: window.Shopify?.locale,
          currency: window.Shopify?.currency,
          country: window.Shopify?.country
        };

        if (
          window.jQuery &&
          typeof window.jQuery.ajax === 'function'
        ) {
          return new Promise((resolve, reject) => {
            window.jQuery.ajax({
              type: 'POST',
              url: '/a/cart-update?page=product',
              dataType: 'json',

              data: {
                product_id: productId,
                cartItems: cartItems,
                config: config
              },

              success: function (response) {
                resolve(response);
              },

              error: function (xhr, status, error) {
                reject(
                  new Error(
                    `KOR request failed: ${
                      error || status || xhr.status
                    }`
                  )
                );
              }
            });
          });
        }

        /*
         * Fetch fallback in case jQuery is not available.
         */
        const formData = new URLSearchParams();

        this.appendKorFormValue(
          formData,
          'product_id',
          productId
        );

        this.appendKorFormValue(
          formData,
          'cartItems',
          cartItems
        );

        this.appendKorFormValue(
          formData,
          'config',
          config
        );

        return fetch('/a/cart-update?page=product', {
          method: 'POST',
          credentials: 'same-origin',

          headers: {
            Accept: 'application/json',
            'Content-Type':
              'application/x-www-form-urlencoded; charset=UTF-8',
            'X-Requested-With': 'XMLHttpRequest'
          },

          body: formData.toString()
        }).then((response) => {
          if (!response.ok) {
            throw new Error(
              `KOR request failed: ${response.status}`
            );
          }

          return response.json();
        });
      }

      /*
       * Recreates jQuery-style nested form parameters for the fetch
       * fallback.
       */
      appendKorFormValue(params, key, value) {
        if (value === undefined || value === null) {
          params.append(key, '');
          return;
        }

        if (Array.isArray(value)) {
          value.forEach((item, index) => {
            this.appendKorFormValue(
              params,
              `${key}[${index}]`,
              item
            );
          });

          return;
        }

        if (
          typeof value === 'object' &&
          !(value instanceof Date)
        ) {
          Object.entries(value).forEach(
            ([childKey, childValue]) => {
              this.appendKorFormValue(
                params,
                `${key}[${childKey}]`,
                childValue
              );
            }
          );

          return;
        }

        params.append(key, String(value));
      }

      /*
       * Find the currently selected variant.
       */
      getCurrentVariantId() {
        const variantInput = this.modalContent.querySelector(
          'form[action*="/cart/add"] input[name="id"]'
        );

        return variantInput?.value
          ? String(variantInput.value)
          : null;
      }

      /*
       * Extract the highest-priority applicable KOR rule.
       *
       * KOR's response priority is:
       *
       * Variant
       * Product
       * Collection
       * Product tag
       * Tag
       * Global
       */
      extractKorRule(response) {
        const data = response?.data;

        if (!data) {
          return null;
        }

        const variantId = this.getCurrentVariantId();

        /*
         * Variant rule first.
         */
        if (Array.isArray(data.variants)) {
          let variantRule = null;

          if (variantId) {
            variantRule = data.variants.find((variant) => {
              const id =
                variant?.variant_id ??
                variant?.variantId ??
                variant?.id ??
                variant?.variant?.id;

              return id && String(id) === variantId;
            });
          }

          if (!variantRule && data.variants.length === 1) {
            variantRule = data.variants[0];
          }

          const normalizedVariant =
            this.normalizeKorRuleContainer(
              variantRule,
              'variant'
            );

          if (normalizedVariant) {
            return normalizedVariant;
          }
        }

        /*
         * Follow KOR's normal priority order.
         */
        const candidates = [
          ['product', data.product],
          ['collection', data.collection],
          ['product_tag', data.product_tag],
          ['tag', data.tag],
          ['global', data.global]
        ];

        for (const [type, container] of candidates) {
          const normalized =
            this.findKorRuleInContainer(
              container,
              type
            );

          if (normalized) {
            return normalized;
          }
        }

        return null;
      }

      findKorRuleInContainer(container, type) {
        if (!container) {
          return null;
        }

        if (Array.isArray(container)) {
          for (const item of container) {
            const result = this.findKorRuleInContainer(
              item,
              type
            );

            if (result) {
              return result;
            }
          }

          return null;
        }

        const direct = this.normalizeKorRuleContainer(
          container,
          type
        );

        if (direct) {
          return direct;
        }

        if (typeof container === 'object') {
          for (const value of Object.values(container)) {
            if (
              value &&
              typeof value === 'object'
            ) {
              const nested =
                this.findKorRuleInContainer(
                  value,
                  type
                );

              if (nested) {
                return nested;
              }
            }
          }
        }

        return null;
      }

      normalizeKorRuleContainer(container, type) {
        if (!container || typeof container !== 'object') {
          return null;
        }

        const info = container.info;

        if (!info || typeof info !== 'object') {
          return null;
        }

        const purchaseLimit = this.korNumber(
          info.purchase_limit
        );

        const minimum = this.korNumber(
          info.min_purchase_limit
        );

        const increment = this.korNumber(
          info.increment_qty
        );

        const remainingQty = this.korNumber(
          info.remainingQty
        );

        const lifetimeLimit = this.korNumber(
          info.life_time_limit
        );

        const hasRule =
          purchaseLimit !== null ||
          minimum !== null ||
          increment !== null ||
          remainingQty !== null ||
          lifetimeLimit !== null;

        if (!hasRule) {
          return null;
        }

        /*
         * KOR already calculates remainingQty using the current cart
         * and lifetime restrictions.
         *
         * Therefore, when both exist, use whichever limit is smaller.
         */
        const maximumCandidates = [];

        if (
          purchaseLimit !== null &&
          purchaseLimit >= 0
        ) {
          maximumCandidates.push(purchaseLimit);
        }

        if (
          remainingQty !== null &&
          remainingQty >= 0
        ) {
          maximumCandidates.push(remainingQty);
        }

        const maximum = maximumCandidates.length
          ? Math.min(...maximumCandidates)
          : null;

        return {
          type,

          minimum:
            minimum !== null && minimum >= 0
              ? minimum
              : 1,

          maximum,

          increment:
            increment !== null && increment > 0
              ? increment
              : null,

          purchaseLimit,

          remainingQty,

          lifetimeLimit,

          messages: container.message || {}
        };
      }

      korNumber(value) {
        if (
          value === undefined ||
          value === null ||
          value === '' ||
          value === 'null' ||
          value === 'undefined'
        ) {
          return null;
        }

        const number = Number(value);

        return Number.isFinite(number)
          ? number
          : null;
      }

      /*
       * Apply KOR limit to Dawn's quantity input.
       */
      applyKorRule() {
        const input = this.getKorQuantityInput();

        if (!input || !this.korRule) {
          return;
        }

        const {
          minimum,
          maximum,
          increment
        } = this.korRule;

        if (
          minimum !== null &&
          Number.isFinite(minimum)
        ) {
          input.min = String(minimum);
          input.dataset.min = String(minimum);
        }

        if (
          maximum !== null &&
          Number.isFinite(maximum)
        ) {
          input.max = String(maximum);
          input.dataset.max = String(maximum);
        } else {
          input.removeAttribute('max');
          delete input.dataset.max;
        }

        if (
          increment !== null &&
          Number.isFinite(increment) &&
          increment > 0
        ) {
          input.step = String(increment);
        }

        /*
         * If somebody already clicked + while KOR was loading,
         * correct the quantity immediately.
         */
        this.enforceKorQuantity(false);

        this.updateKorQuantityButtons();
      }

      getKorQuantityInput() {
        return this.modalContent.querySelector(
          'quantity-input input[name="quantity"], input.quantity__input[name="quantity"], input[name="quantity"]'
        );
      }

      handleKorQuantityClick(event) {
        if (!this.korRule) {
          return;
        }

        const input = this.getKorQuantityInput();

        if (!input) {
          return;
        }

        const plusButton = event.target.closest(
          'button[name="plus"], [data-action="increase-quantity"], [data-quantity="up"]'
        );

        if (plusButton) {
          const current =
            this.korNumber(input.value) || 0;

          const step =
            this.korRule.increment || 1;

          const maximum =
            this.korRule.maximum;

          if (
            maximum !== null &&
            current + step > maximum
          ) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();

            input.value = String(maximum);

            this.updateKorQuantityButtons();

            this.showKorMessage(
              'upper'
            );

            return;
          }
        }

        const minusButton = event.target.closest(
          'button[name="minus"], [data-action="decrease-quantity"], [data-quantity="down"]'
        );

        if (minusButton) {
          const current =
            this.korNumber(input.value) || 0;

          const step =
            this.korRule.increment || 1;

          const minimum =
            this.korRule.minimum;

          if (
            minimum !== null &&
            current - step < minimum
          ) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();

            input.value = String(minimum);

            this.updateKorQuantityButtons();

            this.showKorMessage(
              'lower'
            );

            return;
          }
        }

        /*
         * Dawn changes the value after the click handler runs.
         */
        setTimeout(() => {
          this.enforceKorQuantity(false);
          this.updateKorQuantityButtons();
        }, 0);
      }

      handleKorQuantityInput() {
        if (!this.korRule) {
          return;
        }

        this.enforceKorQuantity(true);
        this.updateKorQuantityButtons();
      }

      /*
       * Returns true when the current quantity is valid.
       */
      enforceKorQuantity(showMessage = true) {
        const input = this.getKorQuantityInput();

        if (!input || !this.korRule) {
          return true;
        }

        let value = this.korNumber(input.value);

        if (value === null) {
          return true;
        }

        const {
          minimum,
          maximum,
          increment
        } = this.korRule;

        if (
          maximum !== null &&
          value > maximum
        ) {
          input.value = String(maximum);

          if (showMessage) {
            this.showKorMessage('upper');
          }

          return false;
        }

        if (
          minimum !== null &&
          value < minimum
        ) {
          input.value = String(minimum);

          if (showMessage) {
            this.showKorMessage('lower');
          }

          return false;
        }

        /*
         * Increment/multiple rule.
         */
        if (
          increment !== null &&
          increment > 0
        ) {
          const base =
            minimum !== null
              ? minimum
              : 0;

          const difference =
            value - base;

          if (
            difference >= 0 &&
            difference % increment !== 0
          ) {
            const corrected =
              base +
              Math.floor(
                difference / increment
              ) *
                increment;

            input.value = String(
              Math.max(
                minimum || 0,
                corrected
              )
            );

            if (showMessage) {
              this.showKorMessage(
                'increment'
              );
            }

            return false;
          }
        }

        return true;
      }

      /*
       * Enforce the rule immediately before Add to cart.
       */
      handleKorSubmit(event) {
        if (this.korResubmitting) {
          return;
        }

        const form = event.target;

        if (
          !form ||
          !form.matches(
            'form[action*="/cart/add"]'
          )
        ) {
          return;
        }

        /*
         * Rule already available: instant validation.
         */
        if (this.korRule) {
          const valid =
            this.enforceKorQuantity(true);

          if (!valid) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
          }

          return;
        }

        /*
         * Rule still loading because the customer opened Quick Add
         * and instantly pressed Add to cart.
         *
         * Wait for the existing request. Do NOT create another one.
         */
        if (this.korRulePromise) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();

          const submitter = event.submitter;

          this.korRulePromise
            .then(() => {
              const valid =
                this.enforceKorQuantity(true);

              if (!valid) {
                return;
              }

              this.korResubmitting = true;

              try {
                if (
                  typeof form.requestSubmit ===
                  'function'
                ) {
                  if (submitter) {
                    form.requestSubmit(submitter);
                  } else {
                    form.requestSubmit();
                  }
                } else {
                  form.submit();
                }
              } finally {
                this.korResubmitting = false;
              }
            })
            .catch((error) => {
              console.warn(
                'KOR Quick Add submit validation failed:',
                error
              );
            });
        }
      }

      updateKorQuantityButtons() {
        if (!this.korRule) {
          return;
        }

        const input = this.getKorQuantityInput();

        if (!input) {
          return;
        }

        const value =
          this.korNumber(input.value) || 0;

        const plusButton =
          this.modalContent.querySelector(
            'quantity-input button[name="plus"], button[name="plus"]'
          );

        const minusButton =
          this.modalContent.querySelector(
            'quantity-input button[name="minus"], button[name="minus"]'
          );

        if (plusButton) {
          const shouldDisable =
            this.korRule.maximum !== null &&
            value >= this.korRule.maximum;

          this.setKorButtonDisabled(
            plusButton,
            shouldDisable
          );
        }

        if (minusButton) {
          const shouldDisable =
            this.korRule.minimum !== null &&
            value <= this.korRule.minimum;

          this.setKorButtonDisabled(
            minusButton,
            shouldDisable
          );
        }
      }

      setKorButtonDisabled(
        button,
        shouldDisable
      ) {
        if (shouldDisable) {
          button.disabled = true;
          button.setAttribute(
            'aria-disabled',
            'true'
          );

          button.dataset.korDisabled =
            'true';

          return;
        }

        /*
         * Only re-enable buttons that WE disabled.
         */
        if (
          button.dataset.korDisabled ===
          'true'
        ) {
          button.disabled = false;
          button.removeAttribute(
            'aria-disabled'
          );

          delete button.dataset.korDisabled;
        }
      }

      /*
       * Show KOR's own configured messages below the quantity field.
       */
      showKorMessage(type) {
        if (!this.korRule) {
          return;
        }

        let html = '';

        if (type === 'upper') {
          html =
            this.korRule.messages
              ?.upper_limit_msg ||
            `You can purchase a maximum of <strong>${this.korRule.maximum}</strong>.`;
        }

        if (type === 'lower') {
          html =
            this.korRule.messages
              ?.lower_limit_msg ||
            `You need to purchase at least <strong>${this.korRule.minimum}</strong>.`;
        }

        if (type === 'increment') {
          html =
            this.korRule.messages
              ?.increment_limit_msg ||
            `Quantity must follow the required increment.`;
        }

        if (!html) {
          return;
        }

        let message =
          this.modalContent.querySelector(
            '.kor-quick-add-message'
          );

        if (!message) {
          message =
            document.createElement('div');

          message.className =
            'kor-quick-add-message';

          message.setAttribute(
            'role',
            'alert'
          );

          message.style.marginTop =
            '0.8rem';

          message.style.padding =
            '0.8rem 1rem';

          message.style.border =
            '1px solid currentColor';

          message.style.borderRadius =
            '4px';

          message.style.fontSize =
            '1.3rem';

          const input =
            this.getKorQuantityInput();

          const target =
            input?.closest(
              '.product-form__input, quantity-input'
            ) || input;

          if (target) {
            target.insertAdjacentElement(
              'afterend',
              message
            );
          }
        }

        message.innerHTML = html;
        message.hidden = false;

        if (this.korMessageTimer) {
          clearTimeout(
            this.korMessageTimer
          );
        }

        this.korMessageTimer =
          setTimeout(() => {
            if (message) {
              message.hidden = true;
            }
          }, 5000);
      }

      /*
       * Cache KOR rules during the current browser session.
       *
       * This makes reopening the same product essentially instant.
       */
      getCachedKorRule(productId) {
        try {
          const raw =
            sessionStorage.getItem(
              `bafde-kor-rule-${productId}`
            );

          if (!raw) {
            return null;
          }

          const parsed = JSON.parse(raw);

          /*
           * Cache only for 5 minutes so remainingQty doesn't become
           * stale for too long.
           */
          if (
            !parsed.timestamp ||
            Date.now() -
              parsed.timestamp >
              5 * 60 * 1000
          ) {
            sessionStorage.removeItem(
              `bafde-kor-rule-${productId}`
            );

            return null;
          }

          return parsed.rule || null;
        } catch (error) {
          return null;
        }
      }

      cacheKorRule(productId, rule) {
        try {
          sessionStorage.setItem(
            `bafde-kor-rule-${productId}`,
            JSON.stringify({
              timestamp: Date.now(),
              rule
            })
          );
        } catch (error) {
          /*
           * Cache failure isn't important.
           */
        }
      }

      /*
       * ============================================================
       * ORIGINAL QUICK ADD FUNCTIONS
       * ============================================================
       */

      setInnerHTML(element, html) {
        element.innerHTML = html;

        /*
         * Reinject script tags so scripts contained in the fetched
         * product HTML are allowed to execute.
         */
        element
          .querySelectorAll('script')
          .forEach((oldScriptTag) => {
            const newScriptTag =
              document.createElement(
                'script'
              );

            Array.from(
              oldScriptTag.attributes
            ).forEach((attribute) => {
              newScriptTag.setAttribute(
                attribute.name,
                attribute.value
              );
            });

            newScriptTag.appendChild(
              document.createTextNode(
                oldScriptTag.innerHTML
              )
            );

            oldScriptTag.parentNode.replaceChild(
              newScriptTag,
              oldScriptTag
            );
          });
      }

      preventVariantURLSwitching() {
        const variantPicker =
          this.modalContent.querySelector(
            'variant-radios, variant-selects'
          );

        if (!variantPicker) {
          return;
        }

        variantPicker.setAttribute(
          'data-update-url',
          'false'
        );
      }

      removeDOMElements() {
        const pickupAvailability =
          this.productElement.querySelector(
            'pickup-availability'
          );

        if (pickupAvailability) {
          pickupAvailability.remove();
        }

        const productModal =
          this.productElement.querySelector(
            'product-modal'
          );

        if (productModal) {
          productModal.remove();
        }

        const modalDialog =
          this.productElement.querySelectorAll(
            'modal-dialog'
          );

        if (modalDialog) {
          modalDialog.forEach(
            (modal) => modal.remove()
          );
        }
      }

      preventDuplicatedIDs() {
        const sectionId =
          this.productElement.dataset.section;

        this.productElement.innerHTML =
          this.productElement.innerHTML.replaceAll(
            sectionId,
            `quickadd-${sectionId}`
          );

        this.productElement
          .querySelectorAll(
            'variant-selects, variant-radios, product-info'
          )
          .forEach((element) => {
            element.dataset.originalSection =
              sectionId;
          });
      }

      removeGalleryListSemantic() {
        const galleryList =
          this.modalContent.querySelector(
            '[id^="Slider-Gallery"]'
          );

        if (!galleryList) {
          return;
        }

        galleryList.setAttribute(
          'role',
          'presentation'
        );

        galleryList
          .querySelectorAll(
            '[id^="Slide-"]'
          )
          .forEach((li) =>
            li.setAttribute(
              'role',
              'presentation'
            )
          );
      }

      updateImageSizes() {
        const product =
          this.modalContent.querySelector(
            '.product'
          );

        if (!product) {
          return;
        }

        const desktopColumns =
          product.classList.contains(
            'product--columns'
          );

        if (!desktopColumns) {
          return;
        }

        const mediaImages =
          product.querySelectorAll(
            '.product__media img'
          );

        if (!mediaImages.length) {
          return;
        }

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (
          product.classList.contains(
            'product--medium'
          )
        ) {
          mediaImageSizes =
            mediaImageSizes.replace(
              '715px',
              '605px'
            );
        } else if (
          product.classList.contains(
            'product--small'
          )
        ) {
          mediaImageSizes =
            mediaImageSizes.replace(
              '715px',
              '495px'
            );
        }

        mediaImages.forEach((img) =>
          img.setAttribute(
            'sizes',
            mediaImageSizes
          )
        );
      }
    }
  );
}